import type { RoundAggregate } from "@engine/round/aggregate";
import Matrix from "algorithm/matrix";
import { singleLayerSProcess } from "algorithm/singleLayerSProcess";
import Vector from "algorithm/vector";
import {
  getMatchingRecommenderLayerInputWithoutPreferenceLists,
  getRecommenderLayerInputUncached,
  getRecommenderPreferenceLists,
  STANDARD_RECOMMENDER_PHASE,
  type RecommenderLayerInput,
  type RecommenderLayerInputWithoutPreferenceLists,
  type RecommenderPhase,
} from "./recommenderPreferences";
import type {
  RecommenderBudgetPortion,
  RecommenderBudgetPortionSet,
} from "./recommenderBudgetPortions";

export type RecommenderLayerResult = {
  recommenderAccountsWithoutMatching: Matrix<"evaluator", "evaluable+hold">;
  recommenderGuaranteesWithoutMatching: Matrix<"evaluator", "evaluable+hold">;
  recommenderAccounts: Matrix<"evaluator", "evaluable+hold">;
  recommenderGuarantees: Matrix<"evaluator", "evaluable+hold">;
  phase2RecommenderLayerInput: RecommenderLayerInputWithoutPreferenceLists;
  remainingBudget: number;
  // The raw solver outputs with one row per budget portion, before the
  // aggregation into real-recommender rows above. Funder attribution needs
  // these: when funders with different proposal exclusions share a
  // recommender, only the portion rows say which lane's dollars reached which
  // proposal. Rows align with `budgetPortions`.
  budgetPortions: RecommenderBudgetPortion[];
  budgetPortionAccountsWithoutMatching: Matrix<"evaluator", "evaluable+hold">;
  budgetPortionAccounts: Matrix<"evaluator", "evaluable+hold">;
};

// A prior solution to seed the solver phases from (singleLayerSProcess's
// initialGuarantees). Used by what-if re-runs whose budgets differ only
// slightly from an already-solved baseline, roughly halving solver epochs.
// Each phase seeds independently — omit a phase's matrix to solve that phase
// cold (callers must do so when that phase's budget delta is within the
// solver tolerance; see the warmStart option doc). Rows are per real
// recommender, so seeds only apply when the budget portions are
// one-per-recommender in recommender order.
export type RecommenderLayerWarmStart = {
  standardGuarantees?: Matrix<"evaluator", "evaluable+hold">;
  matchingGuarantees?: Matrix<"evaluator", "evaluable+hold">;
};

const standardPreferenceListCache = new WeakMap<
  { [evaluableId: string]: number },
  Map<string, Matrix<"tranche", "proposal+hold">>
>();

/**
 * Fold an algorithm result with one row per budget portion back into one row
 * per real recommender. `parentRecommenderIndices` maps each portion row to its
 * destination in the real recommender ordering, so sibling synthetic portions
 * land in the same output row. Callers sum accounts because they are additive
 * dollars, but take the maximum guarantee because it is a shared endorsement
 * level rather than a separate amount for each portion.
 */
function aggregateBudgetPortionsByRecommender(
  budgetPortionMatrix: Matrix<"evaluator", "evaluable+hold">,
  parentRecommenderIndices: number[],
  numRecommenders: number,
  operation: "sum" | "max",
): Matrix<"evaluator", "evaluable+hold"> {
  // When every portion maps to its own recommender in order (the unsplit
  // one-per-recommender path), the aggregation is the identity: each output
  // row sums (or maxes, against an initial 0 — values here are non-negative
  // allocations) exactly one input row. Return the input matrix itself
  // instead of copying it; solver outputs are treated as immutable
  // downstream, so aliasing is safe.
  if (budgetPortionMatrix.n_rows === numRecommenders) {
    let isIdentity = true;
    for (let i = 0; i < parentRecommenderIndices.length; i++) {
      if (parentRecommenderIndices[i] !== i) {
        isIdentity = false;
        break;
      }
    }
    if (isIdentity) {
      return budgetPortionMatrix;
    }
  }
  const aggregated = Matrix.zeros<"evaluator", "evaluable+hold">(
    numRecommenders,
    budgetPortionMatrix.n_cols,
  );
  for (
    let budgetPortionIndex = 0;
    budgetPortionIndex < parentRecommenderIndices.length;
    budgetPortionIndex++
  ) {
    const parentRecommenderIndex = parentRecommenderIndices[budgetPortionIndex];
    if (parentRecommenderIndex == null) {
      continue;
    }
    for (let colIndex = 0; colIndex < budgetPortionMatrix.n_cols; colIndex++) {
      const current = aggregated.get(parentRecommenderIndex, colIndex);
      const next = budgetPortionMatrix.get(budgetPortionIndex, colIndex);
      aggregated.set(
        parentRecommenderIndex,
        colIndex,
        operation === "sum" ? current + next : Math.max(current, next),
      );
    }
  }
  return aggregated;
}

function getBudgetPortionPreferenceLists(
  round: RoundAggregate,
  recommenderLayerInput: RecommenderLayerInputWithoutPreferenceLists,
  budgetPortions: RecommenderBudgetPortion[],
  phase: RecommenderPhase,
): Matrix<"tranche", "proposal+hold">[] {
  if (phase.kind === "standard") {
    let byPortion = standardPreferenceListCache.get(
      recommenderLayerInput.proposalIndicesMap,
    );
    if (byPortion == null) {
      byPortion = new Map();
      standardPreferenceListCache.set(
        recommenderLayerInput.proposalIndicesMap,
        byPortion,
      );
    }
    return budgetPortions.map((budgetPortion) => {
      const key = `${budgetPortion.recommender.id}::${[
        ...budgetPortion.excludedProposalIds,
      ]
        .sort()
        .join(",")}`;
      const cached = byPortion.get(key);
      if (cached) {
        return cached;
      }
      const preferenceList = getRecommenderPreferenceLists(
        round,
        [budgetPortion],
        recommenderLayerInput.proposalMax,
        recommenderLayerInput.proposalIndicesMap,
        phase,
      )[0]!.rename("tranche -> tranche", "evaluable+hold -> proposal+hold");
      byPortion.set(key, preferenceList);
      return preferenceList;
    });
  }
  return getRecommenderPreferenceLists(
    round,
    budgetPortions,
    recommenderLayerInput.proposalMax,
    recommenderLayerInput.proposalIndicesMap,
    phase,
  ).map((matrix) =>
    matrix.rename("tranche -> tranche", "evaluable+hold -> proposal+hold"),
  );
}

/**
 * Run the standard and matching recommender phases for each independently
 * allocated portion of a recommender's budget. When funders have different
 * proposal exclusions, those portions come from splitting the recommender into
 * synthetic recommenders. The results are combined under the real recommender.
 */
export function runRecommenderLayer({
  round,
  phase1RecommenderLayerInput,
  budgetPortionSet,
  excludedProposalIds = {},
  debugLabelPrefix,
  warmStart,
}: {
  round: RoundAggregate;
  phase1RecommenderLayerInput: RecommenderLayerInput;
  budgetPortionSet: RecommenderBudgetPortionSet;
  excludedProposalIds?: { [proposalId: string]: number };
  debugLabelPrefix: string;
  // Seed both solves from a nearby prior solution (see
  // RecommenderLayerWarmStart). Solutions still converge to the same
  // maxRemainingBudget tolerance but are not bit-identical to a cold solve,
  // and the solver skips its cold-start postcondition checks — only use this
  // for display-oriented what-if runs, never for the round's canonical
  // allocation. Ignored when the portion rows don't align with recommenders.
  // CALLER BEWARE: never seed a phase whose budget delta is within the
  // solver's convergence tolerance (DEFAULT_MAX_REMAINING_BUDGET) — the
  // seeded solve can pass the convergence check with zero epochs and freeze
  // the answer at the seed, masking the delta entirely. The two phases see
  // different deltas (phase 1 gets only the standard-lane share), so gate
  // each phase's seed on its own delta and omit the one that falls within
  // tolerance (see shouldWarmStartWhatIfSolves in incrementalEvalsCore).
  warmStart?: RecommenderLayerWarmStart;
}): RecommenderLayerResult {
  const budgetPortions = budgetPortionSet.portions;
  const usePrebuiltPreferenceLists =
    budgetPortionSet.kind === "one-per-recommender";
  // Warm-start matrices are one row per real recommender, which only matches
  // the solver's row space on the one-per-recommender path (synthetic splits
  // change both the row count and grouping, and can differ per run). Each
  // phase's seed is resolved independently; an omitted seed means that phase
  // solves cold.
  const resolveWarmStartSeed = (
    seed: Matrix<"evaluator", "evaluable+hold"> | undefined,
  ): Matrix<"evaluator", "evaluable+hold"> | undefined =>
    usePrebuiltPreferenceLists && seed?.n_rows === budgetPortions.length
      ? seed
      : undefined;
  const standardWarmStartSeed = resolveWarmStartSeed(
    warmStart?.standardGuarantees,
  );
  const matchingWarmStartSeed = resolveWarmStartSeed(
    warmStart?.matchingGuarantees,
  );
  const budgets = new Vector<"recommender">(
    budgetPortions.map((budgetPortion) => budgetPortion.budget),
  );
  const standardBudgets = new Vector<"recommender">(
    budgetPortions.map((budgetPortion) => budgetPortion.standardBudget),
  );
  const parentRecommenderIndices = budgetPortions.map(
    (budgetPortion) => budgetPortion.parentRecommenderIndex,
  );
  const numRecommenders =
    phase1RecommenderLayerInput.orderedRecommenders.length;

  const standardPreferenceLists = usePrebuiltPreferenceLists
    ? phase1RecommenderLayerInput.recommenderPreferenceLists
    : getBudgetPortionPreferenceLists(
        round,
        phase1RecommenderLayerInput,
        budgetPortions,
        STANDARD_RECOMMENDER_PHASE,
      );
  const standardPhaseOutput = singleLayerSProcess(
    standardBudgets.rename("recommender -> evaluator"),
    standardPreferenceLists.map((preferenceList) =>
      preferenceList.rename(
        "tranche -> tranche",
        "proposal+hold -> evaluable+hold",
      ),
    ),
    {
      debugLabel: `${debugLabelPrefix}:phase1`,
      initialGuarantees: standardWarmStartSeed,
    },
  );

  const standardGuaranteesByRecommender = aggregateBudgetPortionsByRecommender(
    standardPhaseOutput.guarantees,
    parentRecommenderIndices,
    numRecommenders,
    "max",
  );
  const matchingPhase: RecommenderPhase = {
    kind: "matching",
    initialGuarantees: standardGuaranteesByRecommender.rename(
      "evaluator -> recommender",
      "evaluable+hold -> evaluable+hold",
    ),
  };
  let phase2RecommenderLayerInput: RecommenderLayerInputWithoutPreferenceLists;
  let matchingPreferenceLists: Matrix<"tranche", "proposal+hold">[];
  if (usePrebuiltPreferenceLists) {
    const fullPhase2Input = getRecommenderLayerInputUncached(
      round,
      excludedProposalIds,
      matchingPhase,
    );
    phase2RecommenderLayerInput = fullPhase2Input;
    matchingPreferenceLists = fullPhase2Input.recommenderPreferenceLists;
  } else {
    phase2RecommenderLayerInput =
      getMatchingRecommenderLayerInputWithoutPreferenceLists(
        round,
        excludedProposalIds,
      );
    matchingPreferenceLists = getBudgetPortionPreferenceLists(
      round,
      phase2RecommenderLayerInput,
      budgetPortions,
      {
        kind: "matching",
        initialGuarantees: standardPhaseOutput.guarantees.rename(
          "evaluator -> recommender",
          "evaluable+hold -> evaluable+hold",
        ),
      },
    );
  }
  const matchingPhaseOutput = singleLayerSProcess(
    budgets.rename("recommender -> evaluator"),
    matchingPreferenceLists.map((preferenceList) =>
      preferenceList.rename(
        "tranche -> tranche",
        "proposal+hold -> evaluable+hold",
      ),
    ),
    {
      debugLabel: `${debugLabelPrefix}:phase2`,
      initialGuarantees: matchingWarmStartSeed,
    },
  );

  return {
    recommenderAccountsWithoutMatching: aggregateBudgetPortionsByRecommender(
      standardPhaseOutput.accounts,
      parentRecommenderIndices,
      numRecommenders,
      "sum",
    ),
    recommenderGuaranteesWithoutMatching: aggregateBudgetPortionsByRecommender(
      standardPhaseOutput.guarantees,
      parentRecommenderIndices,
      numRecommenders,
      "max",
    ),
    recommenderAccounts: aggregateBudgetPortionsByRecommender(
      matchingPhaseOutput.accounts,
      parentRecommenderIndices,
      numRecommenders,
      "sum",
    ),
    recommenderGuarantees: aggregateBudgetPortionsByRecommender(
      matchingPhaseOutput.guarantees,
      parentRecommenderIndices,
      numRecommenders,
      "max",
    ),
    phase2RecommenderLayerInput,
    remainingBudget: matchingPhaseOutput.remainingBudget,
    budgetPortions,
    budgetPortionAccountsWithoutMatching: standardPhaseOutput.accounts,
    budgetPortionAccounts: matchingPhaseOutput.accounts,
  };
}
